import { TruncatedTextWithTooltip } from '@app/components/base/TruncatedTextWithTooltip';
import { createDateTimeColumn } from '@app/components/date/create-date-time-column';
import { ApplyColumnFilter } from '@app/components/table/ApplyColumnFilter';
import { createActionsColumn } from '@app/modules/audit/components/create-actions-column';
import { createStatusColumn } from '@app/modules/audit/components/create-status-column';
import { ShowOnStreamAction } from '@app/modules/audit/components/ShowOnStreamAction';
import { getMentionVerifyMatchTargetFromMention } from '@app/modules/audit/modules/station-audit/components/verify-match/to-verify-match-target';
import { VerifyMatchTarget } from '@app/modules/audit/modules/station-audit/components/verify-match/verify-match-types';
import { VerifyMatchAction } from '@app/modules/audit/modules/station-audit/components/verify-match/VerifyMatchAction';
import { AuditLiveMention } from '@mi-front/core';
import { Box } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { useIntl } from 'react-intl';
import { RowActions } from '../../../../../../../../components/table/RowActions';
import { useActiveStation } from '../../../../../../../../core/stations/hooks/useActiveStation';
import { ConfirmedBySchedule } from '../../../../components/audit-result/ConfirmedBySchedule';
import { MentionConfidence } from '../../../../components/mentions/MentionConfidence';

export interface UseMentionsMatchesColumnsOptions {
  /** Opens the verify-match dialog for the given target. */
  openAt: (target: VerifyMatchTarget) => void;
}

/**
 * Datetime / id / description / status / actions columns for the live-mentions matches table.
 */
export function useMentionsMatchesColumns({
  openAt,
}: UseMentionsMatchesColumnsOptions): MRT_ColumnDef<AuditLiveMention>[] {
  const { $t } = useIntl();
  const station = useActiveStation();

  return [
    createDateTimeColumn<AuditLiveMention>($t, {
      accessor: (mention) => mention.datetime,
      size: 130,
    }),
    {
      header: $t({
        id: 'audit.props.mentionId',
        defaultMessage: 'ID mention',
      }),
      accessorKey: 'id',
      enableColumnFilter: false,
      size: 140,
      Cell: ({ row: { original: mention } }) => (
        <ApplyColumnFilter field="name" value={mention.name}>
          {mention.name}
        </ApplyColumnFilter>
      ),
    },
    {
      header: $t({
        id: 'common.text.description',
        defaultMessage: 'Descripción',
      }),
      size: 200,
      accessorKey: 'description',
      Cell: ({ row: { original: mention } }) => (
        <TruncatedTextWithTooltip value={mention.description} />
      ),
    },
    createStatusColumn<AuditLiveMention>($t, {
      size: 140,
      accessorKey: 'confidence',
      Cell: ({ row: { original: mention } }) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <MentionConfidence value={mention.confidence} />
          {mention.confirmedSchedule && (
            <ConfirmedBySchedule
              schedule={mention.confirmedSchedule}
              matchDatetime={mention.datetime}
              showDate
              schedulesListCategory="mentions"
            />
          )}
        </Box>
      ),
    }),
    createActionsColumn<AuditLiveMention>($t, {
      size: 120,
      Cell: ({ row }) => (
        <RowActions>
          <VerifyMatchAction
            onClick={() => openAt(getMentionVerifyMatchTargetFromMention(row.original))}
          />
          <ShowOnStreamAction
            station={station.slug}
            datetime={row.original.datetime}
            tab="livementions"
          />
        </RowActions>
      ),
    }),
  ];
}
